import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Alert, Box, Button as MuiButton, Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { BackLink } from './back';
import { Link } from 'react-router-dom';
import {
  BooleanInput, Create, DateTimeInput, Edit, SimpleForm, TextInput, Title, TopToolbar, required, useNotify, useRecordContext
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { callAction } from './dataProvider';
import { FileBanner } from './image';
import { FilejetImageInput } from './upload';
import { SeoFields } from './seoFields';

/**
 * Offers: the homepage banner and the page it leads to.
 *
 * The rule the whole screen exists to make visible: a scheduled offer takes
 * over the homepage banner while its window is open, and the default comes
 * back when it closes. Running next season's diaľnica offer used to mean
 * editing a controller whitelist and deploying; here it is two dates.
 */

interface OfferRecord {
  id: number;
  slug: string;
  title: string;
  bannerFileIdentifier: string | null;
  bannerImagePath: string | null;
  bannerAlt: string;
  heroFileIdentifier: string | null;
  heroImagePath: string | null;
  template: string | null;
  body: string;
  useCustomBody: boolean;
  isDefault: boolean;
  isVisible: boolean;
  startsAt: string | null;
  endsAt: string | null;
  weight: number;
  isLive: boolean;
}

/**
 * Offers, split into what is running and what is not.
 *
 * The enabled group is ordered by dragging, because priority only matters
 * relative to the other offers running at the same time - a number in a form
 * field made you hold the whole list in your head to set it. The default lives
 * in the enabled group too: it is the banner shown when nothing else is.
 */
export function OfferList() {
  const notify = useNotify();
  const [offers, setOffers] = useState<OfferRecord[] | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    callAction('offers', 'GET' as never)
      .then(value => setOffers((value as { data: OfferRecord[] }).data))
      .catch(() => notify('Akcie sa nepodarilo načítať.', { type: 'warning' }));
  };

  useEffect(load, []);

  const enabled = (offers ?? []).filter(offer => offer.isVisible);
  const disabled = (offers ?? []).filter(offer => !offer.isVisible);

  const move = (fromId: number, toId: number) => {
    if (offers == null || fromId === toId) return;

    const order = [...enabled];
    const from = order.findIndex(offer => offer.id === fromId);
    const to = order.findIndex(offer => offer.id === toId);
    if (from < 0 || to < 0) return;

    const [moved] = order.splice(from, 1);
    if (moved == null) return;
    order.splice(to, 0, moved);
    setOffers([...order, ...disabled]);
  };

  const persist = async () => {
    if (offers == null) return;
    setSaving(true);
    try {
      await callAction('offers/order', 'PUT', { ids: enabled.map(offer => offer.id) });
      notify('Poradie uložené.', { type: 'success' });
      load();
    } catch {
      notify('Poradie sa nepodarilo uložiť.', { type: 'warning' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (offer: OfferRecord) => {
    try {
      await callAction(`offers/${offer.id}/default`);
      notify('Predvolená akcia zmenená. Tento banner sa zobrazí, keď nebeží žiadna iná.', { type: 'success' });
      load();
    } catch {
      notify('Predvolenú akciu sa nepodarilo zmeniť.', { type: 'warning' });
    }
  };

  if (offers == null) return <Typography sx={{ p: 2 }}>Načítava sa…</Typography>;

  const row = (offer: OfferRecord, draggable: boolean) => (
    <Stack
      key={offer.id}
      direction="row"
      spacing={2}
      alignItems="center"
      draggable={draggable}
      onDragStart={draggable ? () => setDragging(offer.id) : undefined}
      onDragOver={draggable ? (event => {
        event.preventDefault();
        if (dragging != null) move(dragging, offer.id);
      }) : undefined}
      onDragEnd={draggable ? (() => { setDragging(null); void persist(); }) : undefined}
      sx={{
        p: 1,
        borderRadius: 1,
        cursor: draggable ? 'grab' : 'default',
        opacity: dragging === offer.id ? 0.4 : 1,
        bgcolor: dragging === offer.id ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      {draggable
        ? <DragIndicatorIcon fontSize="small" color="disabled" />
        : <Box sx={{ width: 20 }} />}

      <Box sx={{ width: 168, flexShrink: 0 }}>
        <FileBanner id={offer.bannerFileIdentifier} width={168} alt={offer.bannerAlt} />
      </Box>

      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>{offer.title}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          /offer/{offer.slug} · {formatWindow(offer.startsAt, offer.endsAt)}
        </Typography>
      </Stack>

      <OfferStatus offer={offer} />

      {/* Exactly one offer can be the fallback, so this is an action rather
          than a checkbox - the API clears the flag everywhere else. */}
      {offer.isDefault ? (
        <Tooltip title="Zobrazí sa, keď nebeží žiadna iná akcia">
          <Chip size="small" color="primary" icon={<StarIcon />} label="Predvolená" />
        </Tooltip>
      ) : (
        <Tooltip title="Nastaviť ako predvolenú">
          <IconButton size="small" aria-label="Nastaviť ako predvolenú" onClick={() => void setDefault(offer)}>
            <StarBorderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Upraviť">
        <IconButton component={Link} to={`/offers/${offer.id}`} size="small" aria-label="Upraviť akciu">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Card sx={{ mt: 1 }}>
      <Title title="Akcie a bannery" />
      <TopToolbar>
        <MuiButton component={Link} to="/offers/create" startIcon={<AddIcon />} size="small">
          Pridať akciu
        </MuiButton>
      </TopToolbar>

      <CardContent>
        <Typography variant="overline" color="text.secondary">Zapnuté</Typography>
        {enabled.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Žiadna zapnutá akcia — na úvodnej stránke sa nezobrazí banner.
          </Typography>
        ) : (
          <>
            <Alert severity="info" variant="outlined" sx={{ my: 1 }}>
              Ak beží viac akcií naraz, zobrazí sa tá vyššie. Poradie zmeníte ťahaním.
            </Alert>
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              {enabled.map(offer => row(offer, true))}
            </Stack>
          </>
        )}

        <Typography variant="overline" color="text.secondary">Vypnuté</Typography>
        {disabled.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Žiadne vypnuté akcie.</Typography>
        ) : (
          <Stack spacing={0.5} sx={{ opacity: 0.7 }}>
            {disabled.map(offer => row(offer, false))}
          </Stack>
        )}

        {saving && <Typography variant="caption" color="text.secondary">Ukladá sa poradie…</Typography>}
      </CardContent>
    </Card>
  );
}

function OfferStatus({ offer }: { offer: OfferRecord }) {
  if (offer.isLive) return <Chip size="small" color="success" label="Zobrazuje sa teraz" />;
  if (!offer.isVisible) return <Chip size="small" variant="outlined" label="Vypnutá" />;
  if (offer.startsAt != null && new Date(offer.startsAt) > new Date()) {
    return <Chip size="small" color="info" variant="outlined" label="Naplánovaná" />;
  }
  if (offer.endsAt != null && new Date(offer.endsAt) < new Date()) {
    return <Chip size="small" variant="outlined" label="Skončila" />;
  }
  return <Chip size="small" variant="outlined" label="Zapnutá" />;
}

function formatWindow(startsAt: string | null, endsAt: string | null): string {
  if (startsAt == null && endsAt == null) return 'bez obmedzenia';
  const from = startsAt == null ? '…' : new Date(startsAt).toLocaleDateString('sk-SK');
  const to = endsAt == null ? '…' : new Date(endsAt).toLocaleDateString('sk-SK');
  return `${from} – ${to}`;
}

function OfferNotice() {
  const record = useRecordContext<OfferRecord>();
  if (record == null) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%', mb: 2 }}>
      {record.isDefault && (
        <Alert severity="success" variant="outlined">
          Toto je predvolená akcia. Jej banner sa na úvodnej stránke zobrazí vždy, keď práve nebeží
          žiadna naplánovaná akcia.
        </Alert>
      )}
      {record.template != null && !record.useCustomBody && (
        <Alert severity="info" variant="outlined">
          Vstavaná akcia — stránku zatiaľ vykresľuje šablóna z webu. Ak chcete obsah upraviť tu,
          zapnite „Použiť vlastný obsah“ nižšie a napíšte text. Späť sa viete kedykoľvek vrátiť,
          šablóna sa nezmaže.
        </Alert>
      )}
    </Stack>
  );
}

function ScheduleFields() {
  return (
    <>
      <BooleanInput
        source="isVisible"
        label="Zapnutá"
        helperText="Vypnutá akcia sa nezobrazí ani v čase, na ktorý je naplánovaná."
      />
      <DateTimeInput
        source="startsAt"
        label="Začiatok"
        helperText="Nechajte prázdne, ak má akcia bežať hneď."
      />
      <DateTimeInput
        source="endsAt"
        label="Koniec"
        helperText="Nechajte prázdne, ak má bežať, kým ju nevypnete."
      />
    </>
  );
}

function BannerFields() {
  return (
    <>
      {/* Two images, because an offer has two: the strip on the homepage and
          the hero on its own page. They are different artwork, so one field
          for both meant the page stretched the homepage strip across it. */}
      <FilejetImageInput
        source="bannerFileIdentifier"
        label="Banner na úvodnej stránke"
        preview="wide"
        helperText="Široký prúžok, ideálne 968 px alebo viac. PNG, JPEG, WebP, GIF alebo AVIF, do 10 MB."
      />
      <FilejetImageInput
        source="heroFileIdentifier"
        label="Obrázok na stránke akcie"
        preview="wide"
        helperText="Väčší obrázok v hlavičke stránky akcie, ideálne 1296 px. Ak ho nenahráte, použije sa banner."
      />
      <TextInput
        source="bannerAlt"
        label="Popis obrázka"
        fullWidth
        helperText="Text pre čítačky a pre prípad, že sa obrázok nenačíta."
      />
    </>
  );
}

/**
 * The page behind the banner.
 *
 * Built-in offers ship a Twig template, and hiding the editor for them read as
 * "this page cannot be changed". Instead the editor is always here, with a
 * switch that decides which one the site renders. The template is kept either
 * way, so taking a page over is reversible.
 */
function OfferBody() {
  const record = useRecordContext<OfferRecord>();
  const useCustom = useWatch({ name: 'useCustomBody' }) as boolean | undefined;
  if (record == null) return null;

  const builtIn = record.template != null;

  return (
    <>
      {builtIn && (
        <BooleanInput
          source="useCustomBody"
          label="Použiť vlastný obsah namiesto vstavanej šablóny"
          helperText="Kým je vypnuté, stránku vykresľuje pôvodná šablóna a text nižšie sa nepoužije."
        />
      )}
      {builtIn && useCustom === true && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Stránku teraz tvorí text nižšie. Ak ho necháte prázdny, web sa vráti k pôvodnej šablóne —
          prázdna stránka sa nezobrazí.
        </Alert>
      )}
      <RichTextInput source="body" label="Obsah stránky" fullWidth />
    </>
  );
}

function OfferSeo() {
  const record = useRecordContext<OfferRecord>();
  return <SeoFields uri={record == null ? undefined : `offer/${record.slug}`} />;
}

export function OfferEdit() {
  return (
    <Edit
      title="Akcia"
      mutationMode="pessimistic"
      redirect="list"
      actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
    >
      <SimpleForm>
        <OfferNotice />
        <TextInput source="title" label="Názov" validate={required()} fullWidth />
        <BannerFields />
        <ScheduleFields />
        <OfferBody />
        <OfferSeo />
      </SimpleForm>
    </Edit>
  );
}

/**
 * A new offer starts from the layout of a real one.
 *
 * The structure is kept in an editable block (sablona-akcie) taken from the
 * diaľnica page, so a new offer opens with the headings and shape that already
 * work on the site rather than an empty editor. Editing the block changes what
 * the next new offer starts from.
 */
function useOfferTemplate(): string | null {
  const [template, setTemplate] = useState<string | null>(null);

  useEffect(() => {
    callAction('content?kind=block', 'GET' as never)
      .then(value => {
        const blocks = (value as { data: { code: string; body: string }[] }).data;
        setTemplate(blocks.find(block => block.code === 'sablona-akcie')?.body ?? '');
      })
      // A missing template is not worth blocking the form for; the editor just
      // starts empty.
      .catch(() => setTemplate(''));
  }, []);

  return template;
}

export function OfferCreate() {
  const template = useOfferTemplate();

  // Wait for it, so the form is not initialised with an empty body and then
  // silently left that way - react-admin reads defaultValues once.
  if (template == null) return <Typography sx={{ p: 2 }}>Načítava sa…</Typography>;

  return (
    <Create title="Nová akcia" redirect="list">
      <SimpleForm defaultValues={{ isVisible: false, weight: 0, body: template }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Nová akcia je najprv vypnutá, aby ste jej stránku mohli pripraviť. Keď ju zapnete a nastavíte
          obdobie, jej banner prevezme úvodnú stránku počas tohto obdobia.
        </Alert>
        <TextInput source="title" label="Názov" validate={required()} fullWidth />
        <TextInput
          source="slug"
          label="Adresa"
          validate={required()}
          helperText="Malé písmená, číslice a pomlčky. Stránka bude na /offer/tento-text."
          fullWidth
        />
        <BannerFields />
        <ScheduleFields />
        <RichTextInput source="body" label="Obsah stránky" fullWidth />
        <SeoFields />
      </SimpleForm>
    </Create>
  );
}
